/**
 * Wire types for the messages feature (PX-W6.2) — `ConversationMessage` JSON
 * (the `UserMessage` subtype this thread writes) and the `MessagePage`
 * envelope. Fields render defensively: the row's author identity is a hex
 * `userId` (resolved against the users directory for a name); names never
 * arrive on the row.
 */

export interface ApiContextRef {
  type?: string
  id?: string
}

/** One thread row. `type` is the `@JsonValue` enum string (`USER_MESSAGE` …). */
export interface ApiMessage {
  id?: string
  type?: string
  /** ObjectId hex of the author — matches `ApiUser.idHex`, not the slug. */
  userId?: string
  content?: string
  contextIds?: ApiContextRef[]
  createdAt?: number
}

/** `GET /{orgId}/messages` envelope. */
export interface MessagePage {
  items?: ApiMessage[]
  nextCursor?: string
}

/**
 * `POST /{orgId}/messages` body. The wire value of MessageType is the enum
 * string itself (`USER_MESSAGE`).
 *
 * ⚠ `contextIds` must be NON-EMPTY with fully-formed refs (type AND id) —
 * `MessageApplicationService.validateInvariants` 400s
 * `message.contextIds.required` / `message.contextRef.invalid` for EVERY
 * subtype. This is the OPPOSITE of the activity contract (where user-level
 * rows post empty contextIds): messages are a different collection with the
 * inverse invariant. The org thread posts exactly one `orgId` ref.
 */
export interface MessageCreateInput {
  type: 'USER_MESSAGE'
  contextIds: { type: string; id: string }[]
  content: string
}

/** The org thread's context tuple: how the org-level conversation is tagged. */
export const ORG_THREAD_CONTEXT_TYPE = 'orgId' as const
